import { lazy as reactLazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router';
import LoadingSpinner from '@/components/loading-spinner';
import VideoDemo from '@/pages/common/video-demo/VideoDemo';
import FaceRealCheck from '@/pages/common/face-real-check/FaceRealCheck';
import VoiceSynthesis from '@/pages/common/voice-synthesis/VoiceSynthesis';
import RegionContactList from '@/pages/region-contact-list/RegionContactList';
import useAuth from '@/hooks/useAuth';
import App from '@/App';
import background from '@/assets/img/ai-background.png';
import ErrorBoundary from '@/libs/error-boundary/ErrorBoundary';
import Other1 from '@/pages/other1/Other1';
import AiLabHelp from '@/components/ai-lab-help/AiLabHelp';

const AuthRoute = ({ children }) => {
  const { checkLogin, checkRoutePermission } = useAuth();
  useEffect(() => {
    checkLogin();
  }, []);
  const hasPermission = checkRoutePermission();
  if (!hasPermission) {
    return null;
  }
  return children;
};

const AiLabHelpWrapper = ({ children }) => {
  return (
    <>
      {children}
      <AiLabHelp />
    </>
  );
};

const lazy = (component) => {
  const LazyComponent = reactLazy(component);
  function SuspenseComponent(props) {
    return (
      <Suspense fallback={<LoadingSpinner text="加载中..." />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  }
  return SuspenseComponent;
};

const TeachingBlockLayout = lazy(
  () => import('@/pages/teaching-block/layout/TeachingBlockLayout'),
);
const TeachingBlockTabEntry = lazy(
  () => import('@/pages/teaching-block/TeachingBlockTabEntry'),
);
const TeachingBlockCourseDetail = lazy(
  () => import('@/pages/teaching-block/TeachingBlockCourseDetail'),
);
const TeachingBlockCoursePreview = lazy(
  () => import('@/pages/teaching-block/TeachingBlockCoursePreview'),
);
const TeachingBlockCourseShare = lazy(
  () => import('@/pages/teaching-block/TeachingBlockCourseShare'),
);
const WebHome = lazy(() => import('../pages/home/Home')); // 首页
const SXWebHome = lazy(() => import('../pages/home/Home-sxnu')); // 山西定制首页

const Setting = lazy(() => import('../pages/setting/Setting')); //设置

// 在懒加载组件部分添加
const PersonalCenter = lazy(
  () => import('../pages/setting/personal-center/PersonalCenter'),
); // 个人中心
const AiExperience = lazy(
  () => import('../pages/setting/ai-experience/AiExperience'),
); // AI体验

const ModelTrain = lazy(() => import('../pages/model-train/ModelTrain')); // 训练模型
const ModelTrainCls = lazy(() => import('../pages/model-train/mmedu/Cls/Cls')); // 训练模型
const ModelTrainDet = lazy(() => import('../pages/model-train/mmedu/Det/Det')); // 训练模型
const ModelTrainNew = lazy(
  () => import('../pages/model-train-new/ModelTrainNew'),
); // 新版训练模型布局（路由版本）

// ModelTrainNew路由相关组件
const Projects = lazy(
  () => import('../pages/model-train-new/projects/Projects'),
);
const ProjectDetail = lazy(
  () =>
    import('../pages/model-train-new/projects/project-detail/ProjectDetail'),
);
const TrainingStep1 = lazy(
  () =>
    import('../pages/model-train-new/projects/project-detail/training-step1/TrainingStep1'),
);
const TrainingStep2 = lazy(
  () =>
    import('../pages/model-train-new/projects/project-detail/training-step2/TrainingStep2'),
);
const TrainingDetail = lazy(
  () =>
    import('../pages/model-train-new/projects/project-detail/training-detail/TrainingDetail'),
);
const Datasets = lazy(
  () => import('../pages/model-train-new/datasets/Datasets'),
);
const ClassificationDataset = lazy(
  () =>
    import('../pages/model-train-new/datasets/classification-dataset/ClassificationDataset'),
);
const AnnotationDataset = lazy(
  () =>
    import('../pages/model-train-new/datasets/annotation-dataset/AnnotationDataset'),
);

// 通用组件懒加载（从common目录导入）
const Courses = lazy(() => import('../pages/courses/Courses')); //课程——整体
const Class = lazy(() => import('../pages/lesson/Lesson')); //班级——整体
const Embodiment = lazy(() => import('../pages/ju-shen-zhi-neng')); //具身智能——整体
const Ailab = lazy(() => import('../pages/ai-lab')); //AI体验——整体

//原三级目录平铺成两级目录
const CourseDetails = lazy(() => import('../pages/courses/detail')); //课程详情
//ai体验——子项目
const AiChat = lazy(() => import('../pages/common/ai-chat')); //AI智能问答
const TextToImage = lazy(
  () => import('../pages/common/text-to-Image/TextToImage'),
); //AI绘画
const FaceAnalyze = lazy(
  () => import('../pages/common/face-analyze/FaceAnalyze'),
); //AI人脸分析
const PhotoAndMath = lazy(
  () => import('../pages/common/photo-and-math/PhotoAndMath'),
); //AI批改作业
const VoiceIdentify = lazy(
  () => import('../pages/common/voice-identify/VoiceIdentify'),
); //AI语音识别
const TextKnow = lazy(() => import('../pages/common/text-know/TextKnow')); //AI文字识别
const FlappyBird = lazy(() => import('../pages/common/flappy-brid/FlappyBrid')); //AI分鸟大冒险
const WritingPredict = lazy(
  () => import('../pages/common/writing-predict/WritingPredict'),
); //AI简笔画预测
const QuickDraw = lazy(() => import('../pages/common/quick-draw/quick-draw')); //AI涂鸦识别
const TrainImage = lazy(
  () => import('../pages/common/train-image/train-image'),
); //AI图像分类
const TrainAudio = lazy(
  () => import('../pages/common/train-audio/train-audio'),
); //AI声音分类
const TrainPose = lazy(() => import('../pages/common/train-pose/train-post')); //AI姿态分类
const TrainText = lazy(() => import('../pages/common/train-text/train-text')); //AI文本分类
const NumberPredict = lazy(
  () => import('../pages/common/number-predict/NumberPredict'),
); //AI手写数字识别原理
const NeuralNetworks = lazy(
  () => import('../pages/common/neural-networks/NeuralNetworks'),
); //AI神经网络原理
const ImageFix = lazy(() => import('../pages/common/image-fix/ImageFix')); //AI图像修复
const TrainHandPose = lazy(
  () => import('../pages/common/train-handpose/train-handpose'),
); // 手势识别分类

const FaceComparisonDemo = lazy(
  () => import('../pages/common/face-comparison/FaceComparisonDemo'),
); // 人脸比对
const FunSticker = lazy(() => import('../pages/common/fun-sticker/FunSticker')); // 趣味大头贴
const VoiceprintRecognition = lazy(
  () => import('../pages/common/voiceprint-recognition/VoiceprintRecognition'),
); // 声纹识别
const ObjectRecognitionDemo = lazy(
  () => import('../pages/common/object-recognition/ObjectRecognitionDemo'),
); // 物体识别
const SketchMagicPen = lazy(
  () => import('../pages/common/sketch-magic-pen/SketchMagicPen'),
); // 神笔小精灵
const DialectTranslator = lazy(
  () => import('../pages/common/dialect-translator/DialectTranslator'),
); // 转译
const SnowDrive = lazy(() => import('../pages/common/snow-drive/SnowDrive')); // 雪地飞车
const KeypointDetection = lazy(
  () => import('../pages/common/keypoint-detection/KeypointDetection'),
); // 关键点检测

const EventPromotion = lazy(() => import('../pages/event/Event')); // 活动推广

// 导入线性回归组件
const LinearRegression = lazy(
  () => import('../pages/common/linear-regression/LinearRegression'),
);

const RobocodePage = lazy(() => import('../pages/robocode/RoboCode'));
const AIToolsPage = lazy(() => import('../pages/ai-tools/AITools'));
const LearningPage = lazy(() => import('../pages/learning/Learning'));
const LearningDetail = lazy(() => import('../pages/learning/detail'));

// 28 page components
const ChatPage28 = lazy(
  () => import('../pages/common/ai-chat/components/ChatPage'), //AI智能问答
);
const WelcomePage = lazy(
  () => import('../pages/common/ai-chat/components/WelcomePage'),
);
const ListPage = lazy(
  () => import('../pages/common/ai-chat/components/ListPage'),
);
const ModelPage = lazy(
  () => import('../pages/common/ai-chat/components/ModelPage'),
);
const ZmChat28 = lazy(() => import('../pages/common/qing-hua/zhonghang/28'));
const ModelCommunity = lazy(
  () => import('../pages/model-community/ModelCommunity'),
);
const ModelDetial = lazy(
  () => import('../pages/model-community/modelDetial/ModelDetial'),
);
const UploadModel = lazy(
  () => import('../pages/model-community/uploadModel/UploadModel'),
);
const EditorModel = lazy(
  () => import('@/pages/model-community/editModel/EditorModel'),
);

// 山西定制页面 AI+学科
const SXSubject = lazy(() => import('@/pages/sx-subject/subject'));

// AI体验子项目配置
export let aiLabSubProjects = [
  {
    path: 'aiChat',
    component: AiChat,
    name: 'AI智能问答',
    requireAuth: true,
    children: [
      { index: true, component: WelcomePage },
      { path: 'chat/:id', component: ChatPage28 },
      { path: 'list', component: ListPage },
      { path: 'model', component: ModelPage },
    ],
  },
  {
    path: 'textToImage',
    component: TextToImage,
    name: 'AI绘画',
    requireAuth: true,
  },
  {
    path: 'faceAnalyze',
    component: FaceAnalyze,
    name: 'AI人脸分析',
    requireAuth: true,
  },
  {
    path: 'photoAndMath',
    component: PhotoAndMath,
    name: 'AI批改作业',
    requireAuth: true,
  },
  {
    path: 'voiceIdentify',
    component: VoiceIdentify,
    name: 'AI语音识别',
    requireAuth: true,
  },
  {
    path: 'textKnow',
    component: TextKnow,
    name: 'AI文字识别',
    requireAuth: true,
  },
  {
    path: 'flappyBird',
    component: FlappyBird,
    name: 'AI分鸟大冒险',
    requireAuth: true,
  },
  {
    path: 'writingPredict',
    component: WritingPredict,
    name: 'AI简笔画预测',
    requireAuth: true,
  },
  {
    path: 'quickDraw',
    component: QuickDraw,
    name: 'AI涂鸦识别',
    requireAuth: true,
  },
  {
    path: 'trainImage',
    component: TrainImage,
    name: 'AI图像分类',
    requireAuth: true,
  },
  {
    path: 'trainAudio',
    component: TrainAudio,
    name: 'AI声音分类',
    requireAuth: true,
  },
  {
    path: 'trainPose',
    component: TrainPose,
    name: 'AI姿态分类',
    requireAuth: true,
  },
  {
    path: 'trainText',
    component: TrainText,
    name: 'AI文本分类',
    requireAuth: true,
  },
  {
    path: 'trainHandPose',
    component: TrainHandPose,
    name: 'AI手势分类',
    requireAuth: true,
  },
  {
    path: 'numberPredict',
    component: NumberPredict,
    name: 'AI手写数字识别原理',
    requireAuth: true,
  },
  {
    path: 'neuralNetworks',
    component: NeuralNetworks,
    name: 'AI神经网络原理',
    requireAuth: true,
  },
  {
    path: 'imageFix',
    component: ImageFix,
    name: '图像修复',
    requireAuth: true,
  },
  {
    path: 'faceRealCheck',
    component: FaceRealCheck,
    name: '人脸实时检测',
    requireAuth: true,
  },
  {
    path: 'aitts',
    component: VoiceSynthesis,
    name: 'AI语音合成',
    requireAuth: true,
  },
  {
    path: 'aivideo',
    component: VideoDemo,
    name: 'AI视频创作',
    requireAuth: true,
  },
  {
    path: 'faceComparison',
    component: FaceComparisonDemo,
    name: '人脸比对',
    requireAuth: true,
  },
  {
    path: 'funSticker',
    component: FunSticker,
    name: '趣味大头贴',
    requireAuth: true,
  },
  {
    path: 'voiceprintRecognition',
    component: VoiceprintRecognition,
    name: '声纹识别',
    requireAuth: true,
  },
  {
    path: 'objectRecognition',
    component: ObjectRecognitionDemo,
    name: '物体识别',
    requireAuth: true,
  },
  {
    path: 'sketchMagicPen',
    component: SketchMagicPen,
    name: '神笔小精灵',
    requireAuth: true,
  },
  {
    path: 'dialectTranslator',
    component: DialectTranslator,
    name: '转译',
    requireAuth: true,
  },
  {
    path: 'snowDrive',
    component: SnowDrive,
    name: '雪地飞车',
    requireAuth: true,
  },
  {
    path: 'keypointDetection',
    component: KeypointDetection,
    name: '关键点检测',
    requireAuth: true,
  },
  {
    path: 'linearRegression',
    component: LinearRegression,
    name: '线性回归训练器',
    requireAuth: false,
  },
  {
    path: '28',
    component: ZmChat28,
    name: '静态网页生成',
    requireAuth: false,
    children: [
      { index: true, component: WelcomePage },
      { path: 'chat/:id', component: ChatPage28 },
      { path: 'list', component: ListPage },
      { path: 'model', component: ModelPage },
    ],
  },
];

const folderNames = ['weiyubin', 'zhonghang', 'zheng-ming-ze'];

const aiLabQinghuaExample = import.meta.glob([
  '../pages/common/qing-hua/weiyubin/*/index.jsx',
  '../pages/common/qing-hua/zhonghang/*/index.jsx',
  '../pages/common/qing-hua/zheng-ming-ze/*/index.jsx',
]);

for (let key in aiLabQinghuaExample) {
  const folders = folderNames.join('|');
  const reg = new RegExp(
    `(?<=../pages/common/qing-hua/(${folders})?/)(?<folder>.*)(?=/index.jsx)`,
  );
  let name = key.match(reg);

  if (name && name.groups.folder) {
    if (name.groups.folder === '28') continue;
    aiLabSubProjects.push({
      path: name.groups.folder,
      component: lazy(aiLabQinghuaExample[key]),
      name: '测试',
      requireAuth: false,
    });
  }
}

// 生成AI体验子项目路由的函数
const generateAiLabRoutes = (basePath) => {
  return aiLabSubProjects.map(
    ({ path, component: Component, requireAuth, children }) => ({
      path: basePath ? `${basePath}/${path}` : path,
      element: requireAuth ? (
        <AuthRoute>
          <AiLabHelpWrapper>
            <Component />
          </AiLabHelpWrapper>
        </AuthRoute>
      ) : (
        <AiLabHelpWrapper>
          <Component />
        </AiLabHelpWrapper>
      ),
      children: children?.map((child) => ({
        ...child,
        element: <child.component />,
        component: undefined,
      })),
    }),
  );
};

// 统一路由配置
export const unifiedRoutes = [
  // 社区版路由 (/)
  {
    path: '/',
    element: <App />,
    ErrorBoundary: ErrorBoundary,
    children: [
      {
        path: '',
        element: <WebHome />,
      },
      {
        path: 'ailab',
        element: (
          <div
            style={{
              backgroundSize: 'cover',
              backgroundImage: `url(${background})`,
              width: '100%',
              height: '100%',
            }}
          >
            <Outlet />
          </div>
        ),
        children: [
          {
            index: true,
            element: <Ailab />,
          },
          ...generateAiLabRoutes(''),
        ],
      },
      {
        path: 'course',
        element: <Courses />,
      },
      {
        path: 'course/:courseID',
        element: <CourseDetails />,
      },
      {
        path: 'embodiment',
        element: <Embodiment />,
      },
      {
        path: 'class',
        element: (
          <AuthRoute>
            <Class />
          </AuthRoute>
        ),
      },
      {
        path: 'eventPromotion',
        element: <EventPromotion />,
      },
      {
        path: 'libs',
        element: <AIToolsPage />,
      },
      {
        path: 'learning',
        element: <LearningPage />,
      },
      {
        path: 'learning/:courseID',
        element: <LearningDetail />,
      },
      {
        path: 'robocodeLite',
        element: (
          <AuthRoute>
            <RobocodePage />
          </AuthRoute>
        ),
      },
      {
        path: 'modelTrain',
        element: <ModelTrain />,
      },
      {
        path: 'modelTrain/mmedu/cls',
        element: <ModelTrainCls />,
      },
      {
        path: 'modelTrain/mmedu/det',
        element: <ModelTrainDet />,
      },
      {
        path: 'modelTrainNew',
        element: (
          <AuthRoute>
            <ModelTrainNew />
          </AuthRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/modelTrainNew/projects" replace />,
          },
          { path: 'projects', element: <Projects /> },
          { path: 'projects/:projectId', element: <ProjectDetail /> },
          { path: 'projects/:projectId/step1', element: <TrainingStep1 /> },
          { path: 'projects/:projectId/step2', element: <TrainingStep2 /> },
          {
            path: 'projects/:projectId/training',
            element: <TrainingDetail />,
          },
          { path: 'datasets', element: <Datasets /> },
          {
            path: 'datasets/classification/:datasetId?',
            element: <ClassificationDataset />,
          },
          {
            path: 'datasets/annotation/:datasetId?',
            element: <AnnotationDataset />,
          },
        ],
      },
      {
        path: 'setting',
        element: <Setting />,
        children: [
          {
            index: true,
            element: <Navigate to="/setting/personal" replace />,
          },
          {
            path: 'personal',
            element: <PersonalCenter />,
          },
          {
            path: 'experience',
            element: <AiExperience />,
          },
        ],
      },
      {
        path: 'regionContactList',
        element: <RegionContactList />,
      },
      {
        path: 'modelCommunity',
        element: <ModelCommunity />,
      },
      {
        path: 'modelCommunity/:modelID',
        element: <ModelDetial />,
      },
      {
        path: 'modelCommunity/uploadModel',
        element: (
          <AuthRoute>
            <UploadModel />
          </AuthRoute>
        ),
      },
      {
        path: 'modelCommunity/editorModel/:modelID',
        element: (
          <AuthRoute>
            <EditorModel />
          </AuthRoute>
        ),
      },
      {
        path: 'teachingBlock',
        element: <TeachingBlockLayout />,
        children: [
          {
            index: true,
            element: <TeachingBlockTabEntry />,
          },
          {
            path: 'course/:courseId',
            element: <TeachingBlockCourseDetail />,
          },
          {
            path: '*',
            element: <Navigate to="/teachingBlock?tab=home" replace />,
          },
        ],
      },
      {
        path: 'teachingBlock/course/:courseId/preview',
        element: <TeachingBlockCoursePreview />,
      },
      {
        path: 'share/course/:courseId',
        element: <TeachingBlockCourseShare />,
      },

      // 学与玩科技教育
      {
        path: 'playwithai',
        element: <Other1 />,
      },
      //定制版首页
      {
        path: 'sxnu',
        element: <SXWebHome />,
      },
      // 山西师范大学定制版AI+学科页面
      {
        path: 'sx-subject',
        element: <SXSubject />,
      },
      {
        path: '*',
        element: <Navigate to={'/'} />,
      },
    ],
  },
];
